<script setup lang="ts">
import { Analytics } from '../analytics';

const currentYear = new Date().getFullYear();
defineEmits(['navigate']);

// UTM Parameters for tracking
const instagramUrl =
  'https://instagram.com/rifaslafavorita?utm_source=website&utm_medium=footer_icon&utm_campaign=social_traffic&utm_content=footer_instagram';

// WhatsApp URLs with UTM
const whatsappUrls = {
  atencion:
    'https://wa.me/584167452305?text=Hola%20Rifas%20La%20Favorita%2C%20tengo%20una%20consulta&utm_source=website&utm_medium=footer&utm_campaign=atencion_cliente',
  ventas:
    'https://wa.me/584262750225?text=Hola%2C%20quiero%20comprar%20tickets%20para%20el%20sorteo&utm_source=website&utm_medium=footer&utm_campaign=ventas',
  soporte:
    'https://wa.me/584165424374?text=Hola%2C%20necesito%20soporte%20con%20mi%20pago&utm_source=website&utm_medium=footer&utm_campaign=soporte_pagos',
};

const trackContact = (type: string, label: string) => {
  Analytics.trackContact(type as any, label);
};
</script>

<template>
  <footer
    class="bg-black border-t border-white/10 pt-16 pb-8 text-sm text-gray-400"
    role="contentinfo"
    itemscope
    itemtype="https://schema.org/WPFooter"
  >
    <div class="max-w-7xl mx-auto px-6 lg:px-8">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
        <!-- Column 1: Brand & Social -->
        <div class="space-y-6" itemscope itemtype="https://schema.org/Organization">
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-xl bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-lg shadow-gold-500/20"
            >
              <span class="font-black text-black text-lg" itemprop="name">LF</span>
            </div>
            <div class="flex flex-col">
              <span class="font-bold text-white text-lg leading-none">RIFAS</span>
              <span class="font-black text-gold-500 text-lg leading-none">LA FAVORITA</span>
            </div>
          </div>
          <p class="leading-relaxed text-gray-500" itemprop="description">
            La plataforma de sorteos más confiable de Venezuela. Premios reales, transparencia total
            y ganadores semanales.
          </p>
          <meta itemprop="url" content="https://www.lafavoritarifas.com" />

          <!-- Social Icons - Instagram 2024/2026 Official Icon -->
          <div class="flex gap-4">
            <a
              :href="instagramUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="group w-11 h-11 rounded-xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-[2px] hover:shadow-lg hover:shadow-pink-500/40 transition-all duration-300 hover:scale-110"
              aria-label="Seguir a Rifas La Favorita en Instagram"
              @click="trackContact('instagram', 'Footer Icon')"
              itemprop="sameAs"
            >
              <div
                class="w-full h-full rounded-[10px] bg-black flex items-center justify-center group-hover:bg-transparent transition-colors"
              >
                <!-- Instagram Icon 2024 (Gradient Style) -->
                <svg
                  class="w-5 h-5 text-white"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
                  />
                </svg>
              </div>
            </a>

            <!-- Facebook -->
            <a
              href="https://facebook.com/rifaslafavorita?utm_source=website&utm_medium=footer_icon&utm_campaign=social_traffic"
              target="_blank"
              rel="noopener noreferrer"
              class="w-11 h-11 rounded-xl bg-[#1877F2] flex items-center justify-center hover:shadow-lg hover:shadow-blue-500/40 transition-all duration-300 hover:scale-110"
              aria-label="Seguir a Rifas La Favorita en Facebook"
              @click="trackContact('facebook', 'Footer Icon')"
            >
              <svg
                class="w-5 h-5 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill-rule="evenodd"
                  d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                  clip-rule="evenodd"
                />
              </svg>
            </a>

            <!-- TikTok -->
            <a
              href="https://tiktok.com/@rifaslafavorita?utm_source=website&utm_medium=footer_icon&utm_campaign=social_traffic"
              target="_blank"
              rel="noopener noreferrer"
              class="w-11 h-11 rounded-xl bg-black border border-white/20 flex items-center justify-center hover:shadow-lg hover:shadow-white/20 transition-all duration-300 hover:scale-110 hover:border-white/40"
              aria-label="Seguir a Rifas La Favorita en TikTok"
              @click="trackContact('tiktok', 'Footer Icon')"
            >
              <svg
                class="w-5 h-5 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"
                />
              </svg>
            </a>
          </div>
        </div>

        <!-- Column 2: Sorteos & Zona Usuarios -->
        <nav aria-label="Sorteos y zona de usuarios">
          <h3 class="font-bold text-white mb-6 uppercase tracking-widest text-xs">
            Sorteos Activos
          </h3>
          <ul class="space-y-4 mb-8" role="list">
            <li>
              <a
                href="/"
                @click.prevent="$emit('navigate', 'landing')"
                class="group flex items-center gap-2 hover:text-gold-500 transition-colors"
                aria-current="page"
              >
                <span
                  class="w-2 h-2 rounded-full bg-green-500 animate-pulse"
                  aria-hidden="true"
                ></span>
                <span class="font-bold text-white group-hover:text-gold-400"
                  >Gran Sorteo 150 Mautes 🐂</span
                >
              </a>
              <p class="text-xs text-gray-600 mt-1 pl-4">
                150 Mautes en Pie + Ranking Mayor Comprador
              </p>
            </li>
          </ul>

          <h3 class="font-bold text-white mb-6 uppercase tracking-widest text-xs">
            Zona de Usuarios
          </h3>
          <ul class="space-y-4" role="list">
            <li>
              <a
                href="/ganadores"
                @click.prevent="$emit('navigate', 'winners')"
                class="flex items-center gap-2 hover:text-gold-500 transition-colors group"
              >
                <span
                  class="w-6 h-6 rounded bg-gold-500/10 flex items-center justify-center text-gold-500 group-hover:bg-gold-500 group-hover:text-black transition-colors"
                  >🏆</span
                >
                <span>Ganadores Anteriores</span>
              </a>
            </li>
            <li>
              <a
                href="/mis-tickets"
                @click.prevent="$emit('navigate', 'my_tickets')"
                class="flex items-center gap-2 hover:text-gold-500 transition-colors group"
              >
                <span
                  class="w-6 h-6 rounded bg-gold-500/10 flex items-center justify-center text-gold-500 group-hover:bg-gold-500 group-hover:text-black transition-colors"
                  >🎫</span
                >
                <span>Mis Tickets / Verificar</span>
              </a>
            </li>
            <li>
              <a
                href="/como-funciona"
                @click.prevent="$emit('navigate', 'how_it_works')"
                class="flex items-center gap-2 hover:text-gold-500 transition-colors group"
              >
                <span
                  class="w-6 h-6 rounded bg-gold-500/10 flex items-center justify-center text-gold-500 group-hover:bg-gold-500 group-hover:text-black transition-colors"
                  >ℹ️</span
                >
                <span>Cómo Funciona</span>
              </a>
            </li>
          </ul>
        </nav>

        <!-- Column 3: Legal & Soporte -->
        <nav aria-label="Legal y soporte">
          <h3 class="font-bold text-white mb-6 uppercase tracking-widest text-xs">
            Legal & Soporte
          </h3>
          <ul class="space-y-4" role="list">
            <li>
              <a
                href="/terminos-y-condiciones"
                @click.prevent="$emit('navigate', 'terms')"
                class="flex items-center gap-2 hover:text-gold-500 transition-colors group"
              >
                <span
                  class="w-6 h-6 rounded bg-white/5 flex items-center justify-center text-gray-500 group-hover:bg-gold-500/10 group-hover:text-gold-500 transition-colors"
                  >📜</span
                >
                <span>Términos y Condiciones</span>
              </a>
            </li>
            <li>
              <a
                href="/politica-de-privacidad"
                @click.prevent="$emit('navigate', 'privacy')"
                class="flex items-center gap-2 hover:text-gold-500 transition-colors group"
              >
                <span
                  class="w-6 h-6 rounded bg-white/5 flex items-center justify-center text-gray-500 group-hover:bg-gold-500/10 group-hover:text-gold-500 transition-colors"
                  >🔒</span
                >
                <span>Política de Privacidad</span>
              </a>
            </li>
            <li>
              <a
                href="/reglas-del-juego"
                @click.prevent="$emit('navigate', 'rules')"
                class="flex items-center gap-2 hover:text-gold-500 transition-colors group"
              >
                <span
                  class="w-6 h-6 rounded bg-white/5 flex items-center justify-center text-gray-500 group-hover:bg-gold-500/10 group-hover:text-gold-500 transition-colors"
                  >⚖️</span
                >
                <span>Reglas del Juego</span>
              </a>
            </li>
            <li>
              <a
                href="/centro-de-ayuda"
                @click.prevent="$emit('navigate', 'help')"
                class="flex items-center gap-2 hover:text-gold-500 transition-colors group"
              >
                <span
                  class="w-6 h-6 rounded bg-white/5 flex items-center justify-center text-gray-500 group-hover:bg-gold-500/10 group-hover:text-gold-500 transition-colors"
                  >❓</span
                >
                <span>Centro de Ayuda</span>
              </a>
            </li>
            <li>
              <a
                href="/contacto"
                @click.prevent="$emit('navigate', 'contact')"
                class="flex items-center gap-2 hover:text-gold-500 transition-colors group"
              >
                <span
                  class="w-6 h-6 rounded bg-white/5 flex items-center justify-center text-gray-500 group-hover:bg-gold-500/10 group-hover:text-gold-500 transition-colors"
                  >💬</span
                >
                <span>Contacto</span>
              </a>
            </li>
          </ul>
        </nav>

        <!-- Column 4: Contact with WhatsApp Links -->
        <div itemscope itemtype="https://schema.org/ContactPoint">
          <h3 class="font-bold text-white mb-6 uppercase tracking-widest text-xs">Contacto</h3>
          <ul class="space-y-4 font-mono text-xs" role="list">
            <!-- Atención al Cliente -->
            <li class="flex items-center gap-3">
              <a
                :href="whatsappUrls.atencion"
                target="_blank"
                rel="noopener noreferrer"
                class="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center text-white hover:bg-green-500 transition-colors shadow-lg shadow-green-600/20"
                aria-label="WhatsApp Atención al Cliente"
                @click="trackContact('whatsapp', 'Atencion Cliente Footer')"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
                  />
                </svg>
              </a>
              <div>
                <p class="text-gray-500 text-[10px] uppercase font-bold" itemprop="contactType">
                  Atención al Cliente
                </p>
                <a
                  :href="whatsappUrls.atencion"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-white hover:text-gold-500 block"
                  itemprop="telephone"
                  @click="trackContact('whatsapp', 'Atencion Cliente Footer')"
                  >+58 416 745 2305</a
                >
              </div>
            </li>

            <!-- WhatsApp Ventas -->
            <li class="flex items-center gap-3">
              <a
                :href="whatsappUrls.ventas"
                target="_blank"
                rel="noopener noreferrer"
                class="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center text-white hover:bg-green-500 transition-colors shadow-lg shadow-green-600/20"
                aria-label="WhatsApp Ventas"
                @click="trackContact('whatsapp', 'Ventas Footer')"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
                  />
                </svg>
              </a>
              <div>
                <p class="text-gray-500 text-[10px] uppercase font-bold">WhatsApp Ventas</p>
                <a
                  :href="whatsappUrls.ventas"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-white hover:text-gold-500 block"
                  @click="trackContact('whatsapp', 'Ventas Footer')"
                  >+58 426 275 0225</a
                >
              </div>
            </li>

            <!-- Soporte Pagos -->
            <li class="flex items-center gap-3">
              <a
                :href="whatsappUrls.soporte"
                target="_blank"
                rel="noopener noreferrer"
                class="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center text-white hover:bg-green-500 transition-colors shadow-lg shadow-green-600/20"
                aria-label="WhatsApp Soporte Pagos"
                @click="trackContact('whatsapp', 'Soporte Pagos Footer')"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
                  />
                </svg>
              </a>
              <div>
                <p class="text-gray-500 text-[10px] uppercase font-bold">Soporte Pagos</p>
                <a
                  :href="whatsappUrls.soporte"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-white hover:text-gold-500 block"
                  @click="trackContact('whatsapp', 'Soporte Pagos Footer')"
                  >+58 416 542 4374</a
                >
              </div>
            </li>

            <!-- Email -->
            <li class="flex items-center gap-3 pt-2">
              <span
                class="w-9 h-9 rounded-lg bg-gold-500/10 flex items-center justify-center text-gold-500"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </span>
              <a
                href="mailto:contacto@lafavoritarifas.com"
                class="text-white hover:text-gold-500"
                itemprop="email"
                >contacto@lafavoritarifas.com</a
              >
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div
        class="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4"
      >
        <p class="text-center md:text-left text-gray-600 text-xs">
          &copy; {{ currentYear }} Rifas La Favorita. Todos los derechos reservados. Venezuela 🇻🇪
        </p>
        <div class="flex items-center gap-6">
          <span class="text-xs text-gray-600 flex items-center gap-2">
            <span class="w-2 h-2 rounded-full bg-green-500 animate-pulse" aria-hidden="true"></span>
            Sistemas Operativos v2.3 (Mautes)
          </span>
          <!-- SSL Badge -->
          <span class="text-xs text-gray-600 flex items-center gap-1">
            <svg
              class="w-4 h-4 text-green-500"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clip-rule="evenodd"
              />
            </svg>
            SSL Seguro
          </span>
        </div>
      </div>
    </div>
  </footer>
</template>
